import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type { Verdict } from "../../studio/studioTypes"
import { hasConditionsRow } from "../mapping/verdictDisplay"

/**
 * The minimal shape both `WorklistIssue` (`studio/cellState.ts`) and
 * `ApproveBlockingWorkItem` (`approveGateTypes.ts`) satisfy structurally, so
 * this module needs no dependency on either one: any object naming its own
 * step and state can be read by `issueReasonKey`.
 */
export interface ReasonableIssue {
  step: 1 | 2 | 3
  state: "needs-input" | "needs-verification" | "ready"
}

export interface IssueReason {
  /** The chip / bullet / popover-row label: short, always shown. */
  label: TranslationKeys
  /** The hover explanation: what is open and which gesture closes it. Null
   *  when no gesture in the V3 mask can actually close this state, so a
   *  caller renders no tooltip rather than inventing a button that is not
   *  there. Today that is every `ready` state: nothing in the studio writes the
   *  pending-resolution mark `ready` reports on (its only writers were the
   *  retired Studio V1 screens), so no `ready` issue reaches this screen. */
  hint: TranslationKeys | null
}

/**
 * ONE place mapping a worklist issue's step AND state to its reason copy.
 * The collapsed card's chip (`UnitCard`), the open-points
 * popover row (`OpenPointsPopover`) and the approve gate's bullet list
 * (`ApproveGatePanel`) all call this, so the four surfaces can never
 * disagree about what one issue is called. Replaces `STEP_KEY`, which named
 * the step only and could not tell "a required field is empty" apart from
 * "the values are complete but nobody signed them", the product-owner
 * confusion this task starts from (TUR-1211 wave 5, w5-step3-chip).
 *
 * Step 3 splits three ways (product decision): `needs-input` and
 * `needs-verification` name two different situations that used to share one
 * chip text, and `ready` (fields signed, a guided-resolution mark still
 * awaits Continue) never meant either of those, so it gets its own label too.
 *
 * Steps 1 and 2 keep their existing label regardless of state: the
 * `needs-input`/`needs-verification` split at those steps does not change
 * what the reason CHIP says. Their HINT does split by state where the
 * gesture that clears it genuinely differs:
 *   - step 1 needs-input: no verdict is set yet, cleared by the statement's
 *     own verdict dropdown (a per-card gesture).
 *   - step 1 needs-verification: a verdict is set but unsigned (edited,
 *     pending, a reference-list conflict, or an unknown edition citation),
 *     cleared only by "Verordnung als geprüft markieren" below the cards
 *     (a framework-wide gesture: there is no per-card sign-off for step 1
 *     in the V3 mask today).
 *   - step 2 (either state): both causes (no scope picked, or a picked scope
 *     not yet applied) are cleared the same way, opening the article
 *     browser and pressing Apply, so one hint covers both.
 * `ready`, at every step, gets no hint: see `IssueReason.hint`'s own doc.
 *
 * Step 3 `needs-verification` also reads the statement's `verdict` (TUR-1676):
 * its copy points into the substances section and its Confirm button, which
 * only a card with an exception to substantiate renders (`hasConditionsRow`).
 * A plain verdict ("compliant") reaches step 3 too, e.g. on a first-time fill
 * the auto-verify leaves to a human, and there only "Verordnung als geprüft
 * markieren" signs it, so that card gets copy naming that gesture instead.
 * `verdict` undefined (the caller cannot resolve the statement) keeps the
 * substances copy.
 */
export function issueReasonKey(
  issue: ReasonableIssue,
  verdict?: Verdict | null,
): IssueReason {
  if (issue.step === 3) {
    if (issue.state === "needs-input")
      return {
        label: "MC_DECL_V2_GATE_STEP_3_INPUT",
        hint: "MC_DECL_V2_GATE_STEP_3_INPUT_HINT",
      }
    if (issue.state === "ready")
      return { label: "MC_DECL_V2_GATE_STEP_3_READY", hint: null }
    if (verdict !== undefined && !hasConditionsRow(verdict))
      return {
        label: "MC_DECL_V2_GATE_STEP_3_VERIFY_PLAIN",
        hint: "MC_DECL_V2_GATE_STEP_3_VERIFY_PLAIN_HINT",
      }
    return {
      label: "MC_DECL_V2_GATE_STEP_3_VERIFY",
      hint: "MC_DECL_V2_GATE_STEP_3_VERIFY_HINT",
    }
  }
  if (issue.step === 1) {
    if (issue.state === "ready")
      return { label: "MC_DECL_V2_GATE_STEP_1", hint: null }
    const hint: TranslationKeys =
      issue.state === "needs-input"
        ? "MC_DECL_V2_GATE_STEP_1_INPUT_HINT"
        : "MC_DECL_V2_GATE_STEP_1_VERIFY_HINT"
    return { label: "MC_DECL_V2_GATE_STEP_1", hint }
  }
  return {
    label: "MC_DECL_V2_GATE_STEP_2",
    hint: issue.state === "ready" ? null : "MC_DECL_V2_GATE_STEP_2_HINT",
  }
}
